/**
 * AnimatedSplash — the "Stomp" launch animation (concept Frame 02).
 *
 * The brand logo drops in from above, lands with a squash + screen-shake, throws a
 * shockwave ring and a fan of speed streaks, then the "zporti" wordmark slides up before
 * the whole surface fades out. Every animated property is transform/opacity so the whole
 * thing runs on the native driver and stays smooth during a cold start.
 *
 * Plays once, when `start` flips true (driven by the native-splash handoff), then calls
 * `onFinish` so the host can unmount it.
 */

import { useEffect, useMemo, useRef } from 'react';
import { Animated, Easing, View } from 'react-native';
import { Image } from 'expo-image';

import {
  SPLASH_DROP_DISTANCE,
  SPLASH_LOGO_SOURCE,
  SPLASH_STREAKS,
  SPLASH_TIMINGS,
} from './AnimatedSplash.constants';
import { animatedSplashStyles as styles, streakTransformStyles } from './AnimatedSplash.styles';

interface AnimatedSplashProps {
  /** Flip true once the native splash has handed off, to start the timeline. */
  start: boolean;
  /** Called after the fade-out completes so the host can unmount the overlay. */
  onFinish: () => void;
}

export function AnimatedSplash({ start, onFinish }: AnimatedSplashProps) {
  const drop = useRef(new Animated.Value(0)).current;
  const squash = useRef(new Animated.Value(0)).current;
  const ring = useRef(new Animated.Value(0)).current;
  const streak = useRef(new Animated.Value(0)).current;
  const shake = useRef(new Animated.Value(0)).current;
  const word = useRef(new Animated.Value(0)).current;
  const fade = useRef(new Animated.Value(1)).current;

  const hasStarted = useRef(false);

  useEffect(() => {
    if (!start || hasStarted.current) return;
    hasStarted.current = true;

    const impact = Animated.parallel([
      Animated.sequence([
        Animated.timing(squash, {
          toValue: 1,
          duration: SPLASH_TIMINGS.SQUASH_IN,
          easing: Easing.out(Easing.quad),
          useNativeDriver: true,
        }),
        // Fixed-duration recovery with a back-ease for the little overshoot. This was an
        // Animated.spring (friction 4) — but a bouncy spring keeps oscillating at tiny
        // amplitude for ~1s, and Animated.parallel waits for that full settle, so the
        // wordmark step (which runs AFTER impact) only arrived past ~2s even though the hit
        // looked done. A bounded timing keeps the snap while making impact deterministic.
        Animated.timing(squash, {
          toValue: 0,
          duration: SPLASH_TIMINGS.SQUASH_OUT,
          easing: Easing.out(Easing.back(2)),
          useNativeDriver: true,
        }),
      ]),
      Animated.timing(ring, {
        toValue: 1,
        duration: SPLASH_TIMINGS.RING,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(streak, {
        toValue: 1,
        duration: SPLASH_TIMINGS.STREAK,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(shake, {
        toValue: 1,
        duration: SPLASH_TIMINGS.SHAKE,
        easing: Easing.linear,
        useNativeDriver: true,
      }),
    ]);

    Animated.sequence([
      Animated.timing(drop, {
        toValue: 1,
        duration: SPLASH_TIMINGS.DROP,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      impact,
      // Wordmark rises in as a separate beat right after the hit settles (~0.8s in).
      Animated.timing(word, {
        toValue: 1,
        duration: SPLASH_TIMINGS.WORD,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.delay(SPLASH_TIMINGS.HOLD),
      Animated.timing(fade, {
        toValue: 0,
        duration: SPLASH_TIMINGS.FADE_OUT,
        easing: Easing.in(Easing.quad),
        useNativeDriver: true,
      }),
    ]).start(({ finished }) => {
      if (finished) onFinish();
    });
  }, [start, drop, squash, ring, streak, shake, word, fade, onFinish]);

  const logoTranslateY = drop.interpolate({
    inputRange: [0, 1],
    outputRange: [-SPLASH_DROP_DISTANCE, 0],
  });
  const logoOpacity = drop.interpolate({
    inputRange: [0, 0.12, 1],
    outputRange: [0, 1, 1],
  });
  const squashScaleX = squash.interpolate({ inputRange: [0, 1], outputRange: [1, 1.14] });
  const squashScaleY = squash.interpolate({ inputRange: [0, 1], outputRange: [1, 0.82] });
  const squashSettleY = squash.interpolate({ inputRange: [0, 1], outputRange: [0, 6] });

  const ringScale = ring.interpolate({ inputRange: [0, 1], outputRange: [0.25, 2.4] });
  const ringOpacity = ring.interpolate({ inputRange: [0, 0.12, 1], outputRange: [0, 0.85, 0] });

  const streakScale = streak.interpolate({ inputRange: [0, 1], outputRange: [0.2, 1.7] });
  const streakOpacity = streak.interpolate({ inputRange: [0, 0.15, 1], outputRange: [0, 0.95, 0] });

  const shakeTranslateX = shake.interpolate({
    inputRange: [0, 0.15, 0.3, 0.45, 0.6, 0.8, 1],
    outputRange: [0, -4, 4, -3, 2, -1, 0],
  });
  const shakeTranslateY = shake.interpolate({
    inputRange: [0, 0.15, 0.3, 0.45, 0.6, 0.8, 1],
    outputRange: [0, 2, -2, 1, -1, 1, 0],
  });

  const wordTranslateY = word.interpolate({ inputRange: [0, 1], outputRange: [14, 0] });

  const streaks = useMemo(
    () =>
      SPLASH_STREAKS.map((_, index) => (
        <View key={index} style={[styles.streak, streakTransformStyles[`streak${index}`]]} />
      )),
    []
  );

  return (
    // Composite the whole splash subtree into a single layer before applying the fade opacity.
    // Without this the logo (an expo-image on its own hardware layer) fades slightly out of step
    // with the purple surface, so the character + its square appear to linger a beat longer than
    // the background. Flattening to one texture makes everything fade in exact lockstep.
    <Animated.View
      style={[styles.fill, { opacity: fade }]}
      pointerEvents="none"
      renderToHardwareTextureAndroid
      needsOffscreenAlphaCompositing
    >
      <View style={styles.surface}>
        <Animated.View
          style={[
            styles.stage,
            { transform: [{ translateX: shakeTranslateX }, { translateY: shakeTranslateY }] },
          ]}
        >
          <View style={styles.logoZone}>
            <Animated.View
              style={[styles.ring, { opacity: ringOpacity, transform: [{ scale: ringScale }] }]}
            />
            <Animated.View
              style={[
                styles.streakField,
                { opacity: streakOpacity, transform: [{ scale: streakScale }] },
              ]}
            >
              {streaks}
            </Animated.View>
            <Animated.View
              style={{
                opacity: logoOpacity,
                transform: [
                  { translateY: logoTranslateY },
                  { translateY: squashSettleY },
                  { scaleX: squashScaleX },
                  { scaleY: squashScaleY },
                ],
              }}
            >
              <Image source={SPLASH_LOGO_SOURCE} style={styles.logo} contentFit="cover" />
            </Animated.View>
          </View>
          <Animated.Text
            style={[styles.wordmark, { opacity: word, transform: [{ translateY: wordTranslateY }] }]}
          >
            zporti
          </Animated.Text>
        </Animated.View>
      </View>
    </Animated.View>
  );
}
